import { useContext, useState, useEffect } from "react";
import { LinkedAccountsContext } from "../../providers/LinkedAccountsProvider";
import { Button } from "../../components/ui/button";
import { Check, CirclePlusIcon, Link, RefreshCcw, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { Card, CardContent } from "../../components/ui/card";
import { cn, formatUniversalNumber } from "../../lib/utils";
import { Badge } from "../../components/ui/badge";
import LinkedAccountsContent from "./components/LinkedAccountsContent";
import { BookEZAccount } from "./BankingInterface";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { motion } from "framer-motion";
import UploadFilesModal from "./components/UploadFilesModal";
import { UIContext } from "../../providers/UIProvider";

const LinkedAccountSkeleton = () => {
  return (
    <div className="w-full animate-pulse">
      <div className="mt-3 BookEZ_card_bg p-8 border BookEZ_card_border_color BookEZ_card_radius shadow-md">
        <div className="h-6 w-64 bg-gray-200 rounded-md mb-4"></div>
        <div className="space-y-2">
          <div className="h-4 w-full bg-gray-200 rounded-md"></div>
          <div className="h-4 w-3/4 bg-gray-200 rounded-md"></div>
        </div>
        <div className="h-10 w-40 bg-gray-200 rounded-md mt-6"></div>
      </div>
    </div>
  );
};

export const AccountCardSkeleton = () => {
  return (
    <div className="p-4 border BookEZ_card_border_color BookEZ_card_radius animate-pulse">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-5 w-32 bg-gray-200 rounded"></div>
            <div className="h-4 w-4 bg-gray-200 rounded"></div>
          </div>
          <div className="h-4 w-48 bg-gray-200 rounded mb-1"></div>
          <div className="flex items-center justify-between">
            <div className="h-4 w-16 bg-gray-200 rounded"></div>
            <div className="h-4 w-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export interface LinkedAccountsProps {
  title?: string;
}

export const LinkedAccount = ({ title = "Banking" }: LinkedAccountsProps) => {
  const {
    plaidAccounts,
    bookEZAccounts,
    isLinkedModalOpen,
    selectedBookEZAccount,
    selectedPlaidAccount,
    handleLinkAccounts,
    setSelectedBookEZAccount,
    setSelectedPlaidAccount,
    setIsLinkedModalOpen,
    loadingStates,
    HandleOpenLinkModal,
    userAccounts,
    open,
    getLinkToken,
    ready,
    linkToken,
  } = useContext(LinkedAccountsContext);

  const { plaidClientUserId, plaidClientUserName } = useContext(UIContext);
  const [activeTab, setActiveTab] = useState("bookez");
  const [shouldOpenPlaid, setShouldOpenPlaid] = useState(false);
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);

  const handleConnectBank = async (isModalButton?: boolean) => {
    if (isModalButton) {
      setShouldOpenPlaid(false);
      await getLinkToken();
      setShouldOpenPlaid(true);
      setIsLinkedModalOpen(false);
    } else {
      if (plaidAccounts && plaidAccounts?.length > 0) {
        HandleOpenLinkModal(true);
        return;
      } else {
        setShouldOpenPlaid(false);
        await getLinkToken();
        setShouldOpenPlaid(true);
      }
    }
  };

  useEffect(() => {
    if (shouldOpenPlaid && ready && linkToken) {
      open();
      setShouldOpenPlaid(false);
    }
  }, [shouldOpenPlaid, ready, linkToken, open]);

  return (
    <>
      <div className="w-full">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div className="BookEZ_font_heading BookEZ_font_bold BookEZ_font_header_color mb-2">
            {title}
          </div>
          <div className="flex gap-2 flex-wrap">
            {userAccounts && userAccounts.length >= 0 && (
              <Button
                variant={"outline"}
                className="BookEZ_card_bg mt-2 sm:mt-0"
                onClick={() => HandleOpenLinkModal(true)}
                disabled={
                  loadingStates?.linkModal ||
                  !plaidClientUserId ||
                  !plaidClientUserName
                }
              >
                <CirclePlusIcon className="w-4 h-4 mr-2 " />
                Link Account
              </Button>
            )}
            {
              <Button
                variant={"outline"}
                className="BookEZ_card_bg mt-2 sm:mt-0"
                onClick={() => setIsFileUploadModalOpen(true)}
              >
                <Upload className="w-4 h-4 mr-2 " />
                Upload File
              </Button>
            }
          </div>
        </div>

        {loadingStates?.linkModal && <LinkedAccountSkeleton />}

        {userAccounts && userAccounts.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }} // Start with 0 opacity
            animate={{ opacity: 1 }} // Animate to 1 opacity
            transition={{ duration: 1 }} // 1 second fade-in duration
          >
            <div className="mt-3 BookEZ_card_bg p-8 border BookEZ_card_border_color BookEZ_card_radius shadow-md">
              <h3 className="BookEZ_font_body_large BookEZ_BookEZ_font_color_gray_2  mb-4">
                Link your bank accounts and credit cards
              </h3>
              <p className="BookEZ_font_body_size BookEZ_font_color_gray_4 mb-6">
                Connect your bank accounts and credit cards to automatically
                import your business transactions.
              </p>
              {
                <Button
                  variant={"filled"}
                  disabled={
                    !plaidClientUserId ||
                    !plaidClientUserName ||
                    loadingStates?.linkModal
                  }
                  loading={loadingStates?.linkToken}
                  onClick={() => handleConnectBank()}
                >
                  Connect my bank
                </Button>
              }
            </div>
          </motion.div>
        )}

        {!loadingStates?.linkModal &&
          userAccounts &&
          userAccounts.length > 0 && <LinkedAccountsContent />}
      </div>

      {/* uploadFileModal */}
      <UploadFilesModal
        handleChangeUploadModal={setIsFileUploadModalOpen}
        isFileUploadModalOpen={isFileUploadModalOpen}
      />

      <Dialog open={isLinkedModalOpen} onOpenChange={HandleOpenLinkModal}>
        <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-4 sm:p-6">
          <DialogTitle className="BookEZ_font_body_large font-semibold flex items-center gap-2">
            <Link className="h-5 w-5 sm:h-6 sm:w-6" />
            Link Your Accounts
          </DialogTitle>

          <div className="flex-1 overflow-hidden mt-4">
            {/* Mobile/Small Screen - Tabs Layout */}
            <div className="block md:hidden!">
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="h-full flex flex-col"
              >
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger
                    value="bookez"
                    className="flex items-center gap-2"
                  >
                    BookEZ Accounts ({bookEZAccounts?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger
                    value="plaid"
                    className="flex items-center gap-2"
                  >
                    Plaid Accounts({plaidAccounts?.length || 0})
                  </TabsTrigger>
                </TabsList>

                <TabsContent
                  value="bookez"
                  className="flex-1 flex flex-col space-y-4 mt-0"
                >
                  <div className="space-y-3 min-h-[300px] sm:min-h-[400px]! max-h-[400px] overflow-y-auto flex-1 pr-2">
                    {loadingStates?.bookEZAccounts ? (
                      Array.from({ length: 3 }).map((_, index) => (
                        <AccountCardSkeleton key={index} />
                      ))
                    ) : (
                      <>
                        {bookEZAccounts && bookEZAccounts.length > 0 ? (
                          bookEZAccounts.map((account: BookEZAccount) => (
                            <Card
                              key={account.accountId}
                              className={cn(
                                "cursor-pointer border BookEZ_card_radius transition-all duration-200 hover:shadow-md",
                                selectedBookEZAccount === account.accountId
                                  ? "border-2 BookEZ_card_bg_blue dark:bg-[#eff6ff17] border-blue-200"
                                  : "hover:border-gray-300 BookEZ_card_border_color"
                              )}
                              onClick={() =>
                                setSelectedBookEZAccount(account.accountId)
                              }
                            >
                              <CardContent className="p-3 sm:p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1 sm:mb-2!">
                                      <h4 className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2 ">
                                        {account.accountName}
                                      </h4>
                                      {selectedBookEZAccount ===
                                        account.accountId && (
                                        <Check className="h-4 w-4 text-blue-600" />
                                      )}
                                    </div>
                                    <p className="BookEZ_font_body_size BookEZ_font_color_gray_2">
                                      {account.accountSubTypeName}
                                    </p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))
                        ) : (
                          <div className="text-center py-4 BookEZ_font_color_gray_2 ">
                            No accounts available
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {selectedBookEZAccount && (
                    <div className="flex justify-center">
                      <Button
                        onClick={() => setActiveTab("plaid")}
                        className="flex items-center gap-2"
                      >
                        Next: Select Plaid Account
                        <Check className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </TabsContent>

                <TabsContent
                  value="plaid"
                  className="flex-1 flex flex-col space-y-4 mt-0"
                >
                  <div className="space-y-3 min-h-[300px] sm:min-h-[400px]! max-h-[400px] overflow-y-auto flex-1 pr-2">
                    {loadingStates?.plaidAccounts ? (
                      Array.from({ length: 3 }).map((_, index) => (
                        <AccountCardSkeleton key={index} />
                      ))
                    ) : (
                      <>
                        {plaidAccounts &&
                          plaidAccounts?.map((account) => (
                            <Card
                              key={account.account_id}
                              className={cn(
                                "cursor-pointer border BookEZ_card_radius transition-all duration-200 hover:shadow-md",
                                selectedPlaidAccount === account.account_id
                                  ? "border-2 BookEZ_card_green_bg border-green-200"
                                  : "hover:border-gray-300 BookEZ_card_border_color"
                              )}
                              onClick={() =>
                                setSelectedPlaidAccount(account.account_id)
                              }
                            >
                              <CardContent className="p-3 sm:p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                                      <h4 className="BookEZ_font_body_size font-medium text-gray-900">
                                        {account.name}
                                      </h4>
                                      {selectedPlaidAccount ===
                                        account.account_id && (
                                        <Check className="h-4 w-4 text-green-600" />
                                      )}
                                    </div>
                                    <p className="BookEZ_font_body_size BookEZ_font_color_gray_2  mb-1">
                                      {account.official_name}
                                    </p>
                                    <div className="flex items-center justify-between BookEZ_font_body_size">
                                      <span className="BookEZ_font_color_gray_4">
                                        •••• {account.mask}
                                      </span>
                                      <span className="font-medium text-green-600">
                                        {formatUniversalNumber(
                                          account.balances.current
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                      </>
                    )}
                  </div>

                  {selectedPlaidAccount && selectedBookEZAccount && (
                    <div className="text-center p-4 rounded-lg border border-green-200">
                      <div className="flex items-center justify-center gap-2 text-green-700 font-medium">
                        <Check className="h-5 w-5" />
                        Ready to link accounts
                      </div>
                      <p className="text-sm text-green-600 mt-1">
                        BookEZ & Plaid accounts selected
                      </p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>

            {/* Large Screen - Two Column Layout */}
            <div className="hidden md:block!">
              <div className="grid grid-cols-2 gap-8 h-full">
                <div className="space-y-4 overflow-hidden flex flex-col">
                  <div className="flex items-center justify-between">
                    <h3 className="BookEZ_font_body_size_large BookEZ_font_body_medium BookEZ_font_header_color">
                      Your BookEZ Accounts
                    </h3>
                    <Badge variant="secondary" className="text-xs">
                      {bookEZAccounts?.length || 0} accounts
                    </Badge>
                  </div>

                  <div className="space-y-3 min-h-[300px] sm:min-h-[400px]! max-h-[400px] overflow-y-auto flex-1 pr-2">
                    {loadingStates?.bookEZAccounts ? (
                      Array.from({ length: 3 }).map((_, index) => (
                        <AccountCardSkeleton key={index} />
                      ))
                    ) : (
                      <>
                        {bookEZAccounts && bookEZAccounts.length > 0 ? (
                          bookEZAccounts.map((account: BookEZAccount) => (
                            <Card
                              key={account.accountId}
                              className={cn(
                                "cursor-pointer border BookEZ_card_radius transition-all duration-200 hover:shadow-md",
                                selectedBookEZAccount === account.accountId
                                  ? "border-2 BookEZ_card_bg_blue border-blue-200"
                                  : "hover:border-gray-300 BookEZ_card_border_color"
                              )}
                              onClick={() =>
                                setSelectedBookEZAccount(account.accountId)
                              }
                            >
                              <CardContent className="p-3 sm:p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                                      <h4 className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2 ">
                                        {account.accountName}
                                      </h4>
                                      {selectedBookEZAccount ===
                                        account.accountId && (
                                        <Check className="h-4 w-4 text-blue-600" />
                                      )}
                                    </div>
                                    <p className="BookEZ_font_body_size BookEZ_font_color_gray_4">
                                      {account.accountSubTypeName}
                                    </p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))
                        ) : (
                          <div className="text-center py-4 BookEZ_font_color_gray_2 ">
                            No accounts available
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-4 overflow-hidden flex flex-col">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="BookEZ_font_body_size_large BookEZ_font_body_medium BookEZ_font_header_color">
                        Available Plaid Accounts
                      </h3>
                      <Badge variant="secondary" className="text-xs">
                        {plaidAccounts?.length || 0} accounts
                      </Badge>
                    </div>

                    <Button
                      variant={"underline"}
                      disabled={
                        !plaidClientUserId ||
                        !plaidClientUserName ||
                        loadingStates?.linkToken
                      }
                      onClick={() => handleConnectBank(true)}
                      size="sm"
                      className=""
                    >
                      {loadingStates?.linkToken
                        ? "Loading..."
                        : "Add Plaid Account"}
                    </Button>
                  </div>

                  <div className="space-y-3 min-h-[300px] sm:min-h-[400px] max-h-[400px] overflow-y-auto flex-1 pr-2">
                    {loadingStates?.plaidAccounts ? (
                      Array.from({ length: 3 }).map((_, index) => (
                        <AccountCardSkeleton key={index} />
                      ))
                    ) : (
                      <>
                        {plaidAccounts &&
                          plaidAccounts?.map((account) => (
                            <Card
                              key={account.account_id}
                              className={cn(
                                "cursor-pointer border BookEZ_card_radius transition-all duration-200 hover:shadow-md",
                                selectedPlaidAccount === account.account_id
                                  ? "border-2 BookEZ_card_green_bg border-green-200"
                                  : "hover:border-gray-300 BookEZ_card_border_color"
                              )}
                              onClick={() =>
                                setSelectedPlaidAccount(account.account_id)
                              }
                            >
                              <CardContent className="p-3 sm:p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1 sm:mb-2">
                                      <h4 className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_BookEZ_font_color_gray_2 ">
                                        {account.name}
                                      </h4>
                                      {selectedPlaidAccount ===
                                        account.account_id && (
                                        <Check className="h-4 w-4 text-green-600" />
                                      )}
                                    </div>
                                    <p className="BookEZ_font_body_size BookEZ_font_color_gray_4 mb-1">
                                      {account.official_name}
                                    </p>
                                    <div className="flex items-center justify-between BookEZ_font_body_size">
                                      <span className="font_color_gray_4">
                                        •••• {account.mask}
                                      </span>
                                      <span className="font-medium text-green-600">
                                        {formatUniversalNumber(
                                          account.balances.current
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 sm:pt-6 mt-4 border-t BookEZ_card_border_color">
            <div className="w-full sm:w-auto text-center sm:text-left">
              {selectedBookEZAccount && selectedPlaidAccount ? (
                <span className="flex items-center gap-2 text-green-600 BookEZ_font_body_size">
                  <Check className="h-4 w-4" />
                  Ready to link accounts
                </span>
              ) : (
                <span className="flex items-center BookEZ_font_body_size">
                  {window.innerWidth < 1024
                    ? "Use tabs above to select accounts from each service"
                    : "Select one account from each column to link them"}
                </span>
              )}
            </div>

            <div className="flex gap-3 w-full sm:w-auto">
              <Button
                variant="ghost"
                onClick={() => setIsLinkedModalOpen(false)}
                className="flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={handleLinkAccounts}
                disabled={!selectedBookEZAccount || !selectedPlaidAccount}
                className="flex items-center gap-2 flex-1 sm:flex-none"
              >
                <Link className="h-4 w-4" />
                Link Accounts
              </Button>
            </div>
          </div>
          {loadingStates?.createAccount && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-white bg-opacity-75">
              <div className="flex flex-col items-center gap-2">
                <RefreshCcw className="animate-spin h-6 w-6 text-gray-600" />
                <span className="text-gray-600 text-sm font-medium">
                  Linking accounts...
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
